import * as React from 'react'
import { AnyAction, Dispatch } from 'redux';
import { connect } from 'react-redux'
import { AppState } from '../store'
import CardEmptyInfo from '../components/Card/CardEmptyInfo'
import { showConfirmModal } from '../store/modals/actions'
import { IntegrationsController, TransactionsController } from '../controllers'
import ScrollToTopOnMount from '../components/Effects/ScrollToTopOnMount'
import PageLoader from '../components/Page/PageLoader'
import LocalStorage, { LocalStorageKey } from '../LocalStorage';
import { Helmet } from 'react-helmet';
import { withTranslation, WithTranslation } from 'react-i18next';
import ResourceTable, { ResourceTableAction } from '../components/Resource/ResourceTable';
import ResourceTableRow from '../components/Resource/ResourceTableRow';
import ResourceTableRowData from '../components/Resource/ResourceTableRowData';
import { RouteComponentProps } from 'react-router';
import { Transaction, CurrentUser, ResourceListFilterType, IntegrationType, TransactionFilter, TransactionFilters, RemittanceInformationType } from '../types';
import PageContent from '../components/Page/PageContent';
import NumberFormatter from '../utilities/NumberFormatter';
import { Style } from '../styles';
import TopNavigation from '../components/Navigation/TopNavigation';
import PageHeader from '../components/Page/PageHeader';
import moment from '../utilities/Moment';
import RouteHelper from '../helpers/RouteHelper';
import ERoute from '../ERoute';
import Icon from '../components/Icons/Icon';
import SearchHelper from '../helpers/SearchHelper';
import NavigationDetection from '../components/Effects/NavigationDetection';
import OgmHelper from '../helpers/OgmHelper';
import Notification from '../utilities/Notification';

interface IStateToProps {
  currentUser: CurrentUser
}

interface IDispatchToProps {
  showConfirmModal: typeof showConfirmModal
}

type IProps = IStateToProps & IDispatchToProps & WithTranslation & RouteComponentProps<any>

interface IState {
  transactions: Transaction[],
  page: number,
  perPage: number,
  totalPages: number
  didInitialLoad: boolean
  isFetching: boolean
  sortValue: string
  activeFilters: any
  selectedTransactionIds: string[]
  search: string
}

class Transactions extends React.Component<IProps, IState> {
  constructor(props) {
    super(props);

    const { search, page, perPage, filters } = SearchHelper.getSearch(this.props.location.search, TransactionFilters)

    this.state = {
      transactions: [],
      selectedTransactionIds: [],
      page: page,
      perPage: perPage,
      totalPages: 0,
      didInitialLoad: false,
      isFetching: false,
      sortValue: LocalStorage.get(LocalStorageKey.TRANSACTION_SORT_VALUE, 'execution_time_desc'),
      activeFilters: filters,
      search: search
    }

    this.onRouteChange = this.onRouteChange.bind(this)
    this.onTableTransactionClick = this.onTableTransactionClick.bind(this)
    this.onTableActionClick = this.onTableActionClick.bind(this)
    this.onTableSelectionChange = this.onTableSelectionChange.bind(this)
    this.onTableRowSelectionChange = this.onTableRowSelectionChange.bind(this)
    this.onBulkVerifyClick = this.onBulkVerifyClick.bind(this)
    this.onTransactionFormSubmit = this.onTransactionFormSubmit.bind(this)
    this.onTransactionUpdateFormSubmit = this.onTransactionUpdateFormSubmit.bind(this)
    this.onTransactionFiltersChange = this.onTransactionFiltersChange.bind(this)
    this.onTransactionSortValueChange = this.onTransactionSortValueChange.bind(this)
    this.onTransactionSearchChange = this.onTransactionSearchChange.bind(this)
    this.onTransactionSearchSubmit = this.onTransactionSearchSubmit.bind(this)
    this.onTransactionClearFilters = this.onTransactionClearFilters.bind(this)
    this.onAddAccountClick = this.onAddAccountClick.bind(this)
    this.onPageChange = this.onPageChange.bind(this)
    this.onPerPageChange = this.onPerPageChange.bind(this)
  }

  onRouteChange() {
    const { search, page, perPage, filters } = SearchHelper.getSearch(this.props.location.search, TransactionFilters)

    this.setState({ search: search, page: page, perPage: perPage, activeFilters: filters }, () => {
      this.fetchTransactions(page)
    })
  }

  fetchTransactions(page: number) {
    const { search, sortValue, activeFilters, perPage } = this.state
    this.setState({
      isFetching: true
    }, () => {
      TransactionsController
        .getTransactions({ page: page, per_page: perPage, search: search, order: `${sortValue}`, ...activeFilters })
        .then(response => {
          const { transactions, current_page, total_pages, total_entries } = response;

          this.setState({
            transactions: [...transactions],
            page: current_page,
            totalPages: total_pages,
            didInitialLoad: true,
            isFetching: false
          });
        })
        .catch(console.error)
    });
  }

  onTableTransactionClick(transaction: Transaction) {
    this.props.history.push(RouteHelper.process(ERoute.PATH_TRANSACTION, { id: transaction.id }))
  }

  onTableActionClick(key: string, transaction: Transaction) {
    switch (key) {
      default:
        throw Error('[Transactions] Unimplemented onTableActionClick')
    }
  }

  onTableSelectionChange(selectedTransactionIds: string[]) {
    this.setState({ selectedTransactionIds: selectedTransactionIds })
  }

  onTableRowSelectionChange(selected: boolean, transactionId: string) {
    const { selectedTransactionIds } = this.state

    if (selected) {
      this.setState({ selectedTransactionIds: [...selectedTransactionIds, transactionId] })
    } else {
      this.setState({ selectedTransactionIds: selectedTransactionIds.filter(selectedTransactionId => selectedTransactionId !== transactionId) })
    }
  }

  onBulkVerifyClick() {
    const { showConfirmModal, t } = this.props
    const { selectedTransactionIds } = this.state

    showConfirmModal({
      title: t('Transactions::Verify'),
      description: t('Transactions::You are about to mark {{count}} transactions as verified. Are you sure?', { count: selectedTransactionIds.length }),
      action: { label: t('Transactions::Confirm') },
      onConfirm: async () => {
        try {
          await TransactionsController.bulkVerify(selectedTransactionIds)

          this.setState((prevState) => ({
            transactions: prevState.transactions.map(transaction => (
              selectedTransactionIds.includes(transaction.id) ? { ...transaction, manually_verified: true, verified: true } : transaction
            )),
            selectedTransactionIds: []
          }))

          Notification.notifySuccess(t('Transactions::Transactions successfully verified.'))
        } catch (ex) {
          console.error(ex)
          Notification.notifyError(t('Transactions::Oops something went wrong.'))
        }
      }
    })
  }

  onTransactionFormSubmit(transaction: Transaction) {
    const { transactions } = this.state;

    const newTransactions = [transaction, ...transactions];

    this.setState({ transactions: newTransactions });
  }

  onTransactionUpdateFormSubmit(transaction: Transaction) {
    const { transactions } = this.state

    const transactionIndex = transactions.findIndex(c => c.id === transaction.id);

    if (transactionIndex !== -1) {
      transactions[transactionIndex] = transaction
    }

    this.setState({
      transactions: [
        ...transactions,
      ]
    })
  }

  onTransactionFiltersChange(activeFilters: any) {
    this.setState({ page: 1, activeFilters: activeFilters }, () => {
      const { page, activeFilters, search, sortValue } = this.state
      RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, search: search, ...activeFilters, sortValue: sortValue })
    })
  }

  onTransactionSortValueChange(value: string) {
    LocalStorage.set(LocalStorageKey.TRANSACTION_SORT_VALUE, value)
    this.setState({ page: 1, sortValue: value }, () => {
      const { page, activeFilters, search, sortValue } = this.state
      RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, search: search, ...activeFilters, sortValue: sortValue })
    })
  }

  onTransactionSearchChange(search) {
    this.setState({ search: search })
  }

  onTransactionSearchSubmit(search) {
    this.setState({ page: 1, search: search }, () => {
      const { page, activeFilters, search, sortValue } = this.state
      RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, search: search, ...activeFilters, sortValue: sortValue })
    })
  }

  onTransactionClearFilters() {
    this.setState({
      page: 1,
      search: '',
      activeFilters: {}
    }, () => {
      const { page, activeFilters, search, sortValue } = this.state
      RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, search: search, ...activeFilters, sortValue: sortValue })
    })
  }

  async onAddAccountClick() {
    try {
      const { form_data: formData } = await IntegrationsController.getForm(IntegrationType.PONTO)
      window.location.assign(`/auth/ponto?onboarding_details_id=${formData.onboarding_details_id}&action=transactions`);
    } catch (ex) {
      console.error(ex)
    }
  }

  onPageChange(page: number) {
    this.setState({ page: page }, () => {
      const { page, perPage, activeFilters, search, sortValue } = this.state
      RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, perPage: perPage, search: search, ...activeFilters, sortValue: sortValue })
    })
  }

  onPerPageChange(perPage: number) {
    this.setState({ page: 1, perPage: perPage }, () => {
      const { page, perPage, activeFilters, search, sortValue } = this.state
      LocalStorage.set(LocalStorageKey.PER_PAGE, perPage)
      RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, perPage: perPage, search: search, ...activeFilters, sortValue: sortValue })
    })
  }

  render() {
    const { t, currentUser } = this.props
    const { setting } = currentUser.workspace
    const {
      transactions,
      selectedTransactionIds,
      didInitialLoad,
      isFetching,
      activeFilters,
      sortValue,
      search,
      page,
      perPage,
      totalPages
    } = this.state

    const filtersActive = search?.length > 0 || Object.keys(activeFilters).length > 0
    const filters: TransactionFilter[] = [
      { name: 'verified', label: t('Transactions::Verified'), type: ResourceListFilterType.SINGLE_OPTION, options: [{ label: t('Transactions::Yes'), value: String(true) }, { label: t('Transactions::No'), value: String(false) }] },
      { name: 'account_id', label: t('Transactions::Account'), type: ResourceListFilterType.SINGLE_RESOURCE, resourceType: 'account' },
      { name: 'counterpart_name', label: t('Transactions::Counterpart name'), type: ResourceListFilterType.STRING },
      { name: 'counterpart_account_number', label: t('Transactions::Counterpart account number'), type: ResourceListFilterType.STRING },
      { name: 'remittance_information', label: t('Transactions::Remittance information'), type: ResourceListFilterType.STRING },
      { name: 'execution_time', label: t('Transactions::Execution date'), type: ResourceListFilterType.DATE },
      { name: 'amount', label: t('Transactions::Amount'), type: ResourceListFilterType.NUMBER },
      { name: 'currency', label: t('Transactions::Currency'), type: ResourceListFilterType.STRING },
      { name: 'created_at', label: t('Transactions::Created date'), type: ResourceListFilterType.DATE },
    ]

    const promotedBulkActions: ResourceTableAction[] = [
      { icon: 'check-circle', content: t('Transactions::Verify'), onAction: this.onBulkVerifyClick },
    ]

    return (
      <>
        <Helmet>
          <title>{t('Transactions::{{__appName}} | Transactions')}</title>
        </Helmet>

        <TopNavigation
          icon='arrow-right-arrow-left'
          title={t('Transactions::Transactions')}
        />
        <NavigationDetection
          onRouteChange={this.onRouteChange}
        />

        <ScrollToTopOnMount />

        <PageContent className='is-transactions'>
          <PageHeader title={t('Transactions::Transactions')} />
          {!didInitialLoad && <PageLoader />}
          {didInitialLoad && <ResourceTable
            data={transactions}
            headers={[
              { title: '' },
              { title: t('Transactions::Account') },
              { title: t('Transactions::Counterpart name') },
              { title: t('Transactions::Counterpart account number') },
              { title: t('Transactions::Remittance information') },
              { title: t('Transactions::Execution date'), align: 'right' },
              { title: t('Transactions::Amount'), align: 'right' },
            ]}
            renderRow={(transaction: Transaction) => {
              const isPositive = Number(transaction.amount) > 0
              return (
                <ResourceTableRow
                  key={transaction.id}
                  selected={selectedTransactionIds.includes(transaction.id)}
                  onSelectionChange={(selected) => this.onTableRowSelectionChange(selected, transaction.id)}
                >
                  <ResourceTableRowData onClick={() => this.onTableTransactionClick(transaction)} maxWidth='30px'>
                    {transaction.verified && <span style={{ color: Style.color.brandSuccess }}><Icon icon='check' /></span>}
                    {!transaction.verified && <span style={{ color: Style.color.brandDanger }}><Icon icon='close' /></span>}
                  </ResourceTableRowData>
                  <ResourceTableRowData onClick={() => this.onTableTransactionClick(transaction)} maxWidth='150px' ellipse>
                    {transaction?.account?.name}
                  </ResourceTableRowData>
                  <ResourceTableRowData onClick={() => this.onTableTransactionClick(transaction)} maxWidth='150px' ellipse>
                    {transaction?.counterpart_name || '-'}
                  </ResourceTableRowData>
                  <ResourceTableRowData onClick={() => this.onTableTransactionClick(transaction)}>
                    {transaction?.counterpart_account_number || '-'}
                  </ResourceTableRowData>
                  <ResourceTableRowData onClick={() => this.onTableTransactionClick(transaction)} maxWidth='150px' ellipse>
                    {transaction.remittance_information_type === RemittanceInformationType.STRUCTURED && OgmHelper.validate(transaction.remittance_information) ? OgmHelper.format(transaction.remittance_information) : transaction.remittance_information}
                  </ResourceTableRowData>
                  <ResourceTableRowData onClick={() => this.onTableTransactionClick(transaction)} textAlign='right'>
                    {moment(transaction.execution_time).format(setting.date_format)}
                  </ResourceTableRowData>
                  <ResourceTableRowData
                    onClick={() => this.onTableTransactionClick(transaction)}
                    textAlign='right'
                    style={{ fontWeight: 'bold', color: isPositive ? Style.color.brandSuccess : Style.color.brandDanger }}
                  >
                    {NumberFormatter.formatCurrency(transaction.currency, setting.number_format, transaction.amount)}
                  </ResourceTableRowData>
                </ResourceTableRow>
              )
            }}
            renderEmpty={<CardEmptyInfo
              icon={filtersActive ? 'search' : 'arrow-right-arrow-left'}
              description={filtersActive ? t('Transactions::No transactions found') : t('Transactions::No transactions have been added yet')}
              descriptionActionText={filtersActive ? t('Transactions::Clear filters') : t('Transactions::Add account')}
              onDescriptionActionClick={filtersActive ? this.onTransactionClearFilters : this.onAddAccountClick}
            />}
            filters={filters}
            activeFilters={activeFilters}
            onFiltersChange={this.onTransactionFiltersChange}
            selectedItems={selectedTransactionIds}
            onSelectionChange={this.onTableSelectionChange}
            promotedBulkActions={promotedBulkActions}
            sortOptions={[
              { label: '-', value: '-' },
              { label: t('Transactions::Name (A-Z)'), value: 'counterpart_name_asc' },
              { label: t('Transactions::Name (Z-A)'), value: 'counterpart_name_desc' },
              { label: t('Transactions::Execution date ↑'), value: 'execution_time_asc' },
              { label: t('Transactions::Execution date ↓'), value: 'execution_time_desc' },
              { label: t('Transactions::Created at ↑'), value: 'created_at_asc' },
              { label: t('Transactions::Created at ↓'), value: 'created_at_desc' },
            ]}
            sortValue={sortValue}
            onSortChange={this.onTransactionSortValueChange}
            pagination={{ page: page, perPage: perPage, pageCount: totalPages }}
            onPageChange={this.onPageChange}
            onPerPageChange={this.onPerPageChange}
            isLoading={isFetching}
            stickyHeader={true}
            searchValue={search}
            onSearchChange={this.onTransactionSearchChange}
            onSearchSubmit={this.onTransactionSearchSubmit}
            maxHeight='55vh'
          />}
        </PageContent>
      </>
    )
  }
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const {
    authentication: {
      currentUser,
    }
  } = state

  return {
    currentUser: currentUser,
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
  return {
    showConfirmModal: (options) => dispatch(showConfirmModal(options)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(Transactions))